import React, { useCallback, useEffect, useMemo, useState } from "react";
import QmsDocumentLibraryStaffPanel from "./QmsDocumentLibraryStaffPanel";
import {
  applyAssignedIdSetToStaffRows,
  areAssignedIdSetsEqual,
  getAssignedStaffIdSet,
} from "./qmsPublishStaffUtils";

function QmsPublishStaffDraftPanel({
  staff = [],
  documentId,
  loading = false,
  saving = false,
  onSave,
  className = "qms-publish-ml-staff",
}) {
  const [draftIds, setDraftIds] = useState(() => new Set());
  const [savingStaffIds, setSavingStaffIds] = useState([]);

  const savedIds = useMemo(() => getAssignedStaffIdSet(staff), [staff]);
  const displayStaff = useMemo(
    () => applyAssignedIdSetToStaffRows(staff, draftIds),
    [staff, draftIds],
  );
  const isSaving = saving || savingStaffIds.length > 0;

  useEffect(() => {
    setDraftIds(new Set(savedIds));
    setSavingStaffIds([]);
  }, [documentId]);

  useEffect(() => {
    if (!isSaving) {
      setDraftIds(new Set(savedIds));
    }
  }, [savedIds, isSaving]);

  const applyAndSave = useCallback(
    async (staffRows, assign) => {
      if (isSaving || !staffRows?.length) return;

      const nextIds = new Set(draftIds);
      const affectedIds = [];

      staffRows.forEach((row) => {
        const id = String(row.StaffId);
        affectedIds.push(id);
        if (assign) nextIds.add(id);
        else nextIds.delete(id);
      });

      if (areAssignedIdSetsEqual(nextIds, savedIds)) return;

      setDraftIds(nextIds);
      setSavingStaffIds(affectedIds);

      try {
        await onSave?.(nextIds);
      } catch {
        setDraftIds(new Set(savedIds));
      } finally {
        setSavingStaffIds([]);
      }
    },
    [draftIds, isSaving, onSave, savedIds],
  );

  return (
    <div className={className}>
      <QmsDocumentLibraryStaffPanel
        active
        embedded
        hideFilters
        sortAssignedFirst
        staff={displayStaff}
        loading={loading}
        savingStaffIds={savingStaffIds}
        canManageStaff
        onToggleStaff={(staffRow, assign) => applyAndSave([staffRow], assign)}
        onBulkToggleStaff={(staffRows, assign) => applyAndSave(staffRows, assign)}
      />

      <div className="qms-publish-ml-staff__save-bar qms-publish-ml-staff__save-bar--auto">
        <div className="qms-publish-ml-staff__save-copy">
          <strong>{draftIds.size}</strong> staff selected
          {isSaving ? (
            <span className="qms-publish-ml-staff__save-pending">
              <i className="fas fa-spinner fa-spin" aria-hidden="true" />
              Saving…
            </span>
          ) : (
            <span className="qms-publish-ml-staff__save-saved">Saved</span>
          )}
        </div>
      </div>
    </div>
  );
}

export default QmsPublishStaffDraftPanel;
